import { useId, type CSSProperties, type HTMLAttributes, type SVGProps } from 'react'

export interface IconProps extends SVGProps<SVGSVGElement> {
  /** Icon size in pixels (applied to both width and height) */
  size?: number
}

// ───────────────────────────────────────────
// Field type icons — colored backgrounds with white symbols
// ───────────────────────────────────────────

// Each icon keeps its size (shrink-0): beside a long label in a flex row it
// would otherwise squeeze, clipping its chip and shifting it out of line.
interface FieldTypeIconProps {
  size?: number
  className?: string
}

export function TextFieldIcon({ size = 14, className }: FieldTypeIconProps) {
  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center rounded-sm bg-[color:var(--surface-info-subtle)] text-[color:var(--text-info)] font-semibold ${className ?? ''}`}
      style={{ width: size, height: size, fontSize: size * 0.55 }}
      aria-hidden="true"
    >
      Aa
    </span>
  )
}

export function NumberFieldIcon({ size = 14, className }: FieldTypeIconProps) {
  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center rounded-sm tone-purple bg-[color:var(--tone-bg-subtle)] text-[color:var(--tone-icon)] font-semibold ${className ?? ''}`}
      style={{ width: size, height: size, fontSize: size * 0.65 }}
      aria-hidden="true"
    >
      #
    </span>
  )
}

export function CurrencyFieldIcon({ size = 14, className }: FieldTypeIconProps) {
  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center rounded-sm tone-green bg-[color:var(--tone-bg-subtle)] text-[color:var(--tone-icon)] font-semibold ${className ?? ''}`}
      style={{ width: size, height: size, fontSize: size * 0.65 }}
      aria-hidden="true"
    >
      $
    </span>
  )
}

export function DateFieldIcon({ size = 14, className }: FieldTypeIconProps) {
  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center rounded-sm tone-orange bg-[color:var(--tone-bg-subtle)] text-[color:var(--tone-icon)] font-semibold ${className ?? ''}`}
      style={{ width: size, height: size, fontSize: size * 0.65 }}
      aria-hidden="true"
    >
      <svg width={size * 0.6} height={size * 0.6} viewBox="0 0 10 10" fill="currentColor">
        <rect
          x="1"
          y="2"
          width="8"
          height="7"
          rx="1"
          stroke="currentColor"
          strokeWidth="1"
          fill="none"
        />
        <line x1="1" y1="4" x2="9" y2="4" stroke="currentColor" strokeWidth="1" />
        <line x1="3" y1="0.5" x2="3" y2="2.5" stroke="currentColor" strokeWidth="1" />
        <line x1="7" y1="0.5" x2="7" y2="2.5" stroke="currentColor" strokeWidth="1" />
      </svg>
    </span>
  )
}

export function TimeFieldIcon({ size = 14, className }: FieldTypeIconProps) {
  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center rounded-sm tone-orange bg-[color:var(--tone-bg-subtle)] text-[color:var(--tone-icon)] font-semibold ${className ?? ''}`}
      style={{ width: size, height: size, fontSize: size * 0.65 }}
      aria-hidden="true"
    >
      <svg
        width={size * 0.65}
        height={size * 0.65}
        viewBox="0 0 10 10"
        fill="none"
        stroke="currentColor"
        strokeWidth="1"
      >
        <circle cx="5" cy="5" r="4" />
        <path d="M5 2.5V5L7 6" />
      </svg>
    </span>
  )
}

export function DurationFieldIcon({ size = 14, className }: FieldTypeIconProps) {
  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center rounded-sm tone-purple bg-[color:var(--tone-bg-subtle)] text-[color:var(--tone-icon)] font-semibold ${className ?? ''}`}
      style={{ width: size, height: size, fontSize: size * 0.65 }}
      aria-hidden="true"
    >
      <svg
        width={size * 0.6}
        height={size * 0.65}
        viewBox="0 0 10 10"
        fill="none"
        stroke="currentColor"
        strokeWidth="1"
      >
        <path d="M2 1h6M2 9h6M3 1c0 2 1 2.5 2 4-1 1.5-2 2-2 4M7 1c0 2-1 2.5-2 4 1 1.5 2 2 2 4" />
      </svg>
    </span>
  )
}

export function BooleanFieldIcon({ size = 14, className }: FieldTypeIconProps) {
  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center rounded-sm tone-teal bg-[color:var(--tone-bg-subtle)] text-[color:var(--tone-icon)] font-semibold ${className ?? ''}`}
      style={{ width: size, height: size, fontSize: size * 0.65 }}
      aria-hidden="true"
    >
      <svg
        width={size * 0.6}
        height={size * 0.6}
        viewBox="0 0 10 10"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <polyline points="2,5 4.5,7.5 8,3" />
      </svg>
    </span>
  )
}

/** Returns the appropriate field type icon for a column type */
export function FieldTypeIcon({ type, size = 14 }: { type: string; size?: number }) {
  switch (type) {
    case 'number':
      return <NumberFieldIcon size={size} />
    case 'currency':
      return <CurrencyFieldIcon size={size} />
    case 'date':
    case 'datetime':
      return <DateFieldIcon size={size} />
    case 'time':
      return <TimeFieldIcon size={size} />
    case 'duration':
      return <DurationFieldIcon size={size} />
    case 'boolean':
      return <BooleanFieldIcon size={size} />
    default:
      return <TextFieldIcon size={size} />
  }
}

type CheckboxSize = 'small' | 'medium' | 'large' | 'custom'

interface CheckboxIconProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  size?: CheckboxSize | number
  sizeCustom?: number | string
  size_custom?: number | string
  checked?: boolean
  indeterminate?: boolean
  borderStrokeWidth?: number
}

const CHECKBOX_SIZE_PRESETS: Record<Exclude<CheckboxSize, 'custom'>, number> = {
  small: 12,
  medium: 14,
  large: 16,
}
const CHECKBOX_RADIUS = 4

function resolveCheckboxSize(
  size: CheckboxSize | number,
  sizeCustom?: number | string,
  size_custom?: number | string
): number | string {
  if (typeof size === 'number') return size
  if (size === 'custom') return sizeCustom ?? size_custom ?? CHECKBOX_SIZE_PRESETS.medium
  return CHECKBOX_SIZE_PRESETS[size]
}

// Row number checkbox icon (for hover state)
export function CheckboxIcon({
  size = 'medium',
  sizeCustom,
  size_custom,
  checked = false,
  indeterminate = false,
  className,
  borderStrokeWidth = 1.5,
  style,
  ...props
}: CheckboxIconProps) {
  const isActive = checked || indeterminate
  const resolvedSize = resolveCheckboxSize(size, sizeCustom, size_custom)
  const rootStyle: CSSProperties = {
    alignItems: 'center',
    backgroundColor: isActive ? '#166ee1' : 'transparent',
    borderColor: isActive ? '#166ee1' : 'currentColor',
    borderRadius: CHECKBOX_RADIUS,
    borderStyle: 'solid',
    borderWidth: borderStrokeWidth,
    boxSizing: 'border-box',
    display: 'inline-flex',
    flexShrink: 0,
    height: resolvedSize,
    justifyContent: 'center',
    position: 'relative',
    width: resolvedSize,
    ...style,
  }

  return (
    <span
      className={className}
      style={rootStyle}
      aria-hidden={props['aria-hidden'] ?? true}
      {...props}
    >
      {checked && (
        <span
          style={{
            borderBottom: '2px solid white',
            borderRight: '2px solid white',
            height: '52%',
            marginTop: '-10%',
            transform: 'rotate(45deg)',
            width: '28%',
          }}
        />
      )}
      {indeterminate && !checked && (
        <span
          style={{
            backgroundColor: 'white',
            borderRadius: 999,
            height: 2,
            width: '58%',
          }}
        />
      )}
    </span>
  )
}

// Filter/funnel icon
export function FilterIcon({ size = 24, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.35"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M4.5 5.5h15l-5.8 6.8v6.1l-3.4-1.7v-4.4L4.5 5.5Z" />
    </svg>
  )
}

export function CircleIcon({ size = 24, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 22 22"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M11 21C16.5228 21 21 16.5228 21 11C21 5.47715 16.5228 1 11 1C5.47715 1 1 5.47715 1 11C1 16.5228 5.47715 21 11 21Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export function EyeIcon({ size = 24, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24.5 18.5"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M1.25 9.25C1.25 9.25 5.25 1.25 12.25 1.25C19.25 1.25 23.25 9.25 23.25 9.25C23.25 9.25 19.25 17.25 12.25 17.25C5.25 17.25 1.25 9.25 1.25 9.25Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12.25 12.25C13.9069 12.25 15.25 10.9069 15.25 9.25C15.25 7.59315 13.9069 6.25 12.25 6.25C10.5931 6.25 9.25 7.59315 9.25 9.25C9.25 10.9069 10.5931 12.25 12.25 12.25Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export function ChevronDownIcon({ size = 24, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 14.5 8.5"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M1.25 1.25L7.25 7.25L13.25 1.25"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/**
 * A back arrow: `ArrowLeftIcon`'s head and stroke with a longer stem, for a
 * single back link beside its text. `size` is its height; it's wider.
 */
export function BackArrowIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg
      width={(size * 18) / 16}
      height={size}
      viewBox="0 0 18 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M7 3L2 8l5 5M2 8h14" />
    </svg>
  )
}

export function ArrowRightIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M8 3l5 5-5 5M3 8h10" />
    </svg>
  )
}

export function ArrowLeftIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M10 3L5 8l5 5M5 8h9" />
    </svg>
  )
}

/**
 * Three bars drawn on the pixel grid of the size it renders at: each starts on
 * a whole pixel, a whole number of pixels from the next. A bar that falls
 * between pixel rows draws its rounded ends brighter than its middle, so the
 * three would look unlike each other.
 */
export function MenuIcon({ size = 24, ...props }: IconProps) {
  // Stroke weight as the other icons have it: 2 units of a 24-unit square.
  const thickness = size / 12
  const step = Math.round((size * 5) / 16)
  const top = Math.round((size - 2 * step - thickness) / 2)
  const inset = Math.round(size / 16)
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      {[0, 1, 2].map((bar) => (
        <rect
          key={bar}
          x={inset}
          y={top + bar * step}
          width={size - 2 * inset}
          height={thickness}
          rx={thickness / 2}
          fill="currentColor"
        />
      ))}
    </svg>
  )
}

export function EditIcon({ size = 24, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 22.1213 22.1213"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M10 3.12132H3C2.46957 3.12132 1.96086 3.33203 1.58579 3.70711C1.21071 4.08218 1 4.59089 1 5.12132V19.1213C1 19.6518 1.21071 20.1605 1.58579 20.5355C1.96086 20.9106 2.46957 21.1213 3 21.1213H17C17.5304 21.1213 18.0391 20.9106 18.4142 20.5355C18.7893 20.1605 19 19.6518 19 19.1213V12.1213M17.5 1.62132C17.8978 1.2235 18.4374 1 19 1C19.5626 1 20.1022 1.2235 20.5 1.62132C20.8978 2.01915 21.1213 2.55871 21.1213 3.12132C21.1213 3.68393 20.8978 4.2235 20.5 4.62132L11 14.1213L7 15.1213L8 11.1213L17.5 1.62132Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export function DownloadIcon({ size = 24, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M19 13V17C19 17.5304 18.7893 18.0391 18.4142 18.4142C18.0391 18.7893 17.5304 19 17 19H3C2.46957 19 1.96086 18.7893 1.58579 18.4142C1.21071 18.0391 1 17.5304 1 17V13M5 8L10 13M10 13L15 8M10 13V1"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export function UploadIcon({ size = 24, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M19 13V17C19 17.5304 18.7893 18.0391 18.4142 18.4142C18.0391 18.7893 17.5304 19 17 19H3C2.46957 19 1.96086 18.7893 1.58579 18.4142C1.21071 18.0391 1 17.5304 1 17V13M15 6L10 1M10 1L5 6M10 1V13"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export function CoinsIcon({ size = 24, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 21 22"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M12.1755 3.22225C12.1766 1.99445 9.67307 1 6.58832 1C3.50357 1 1.00224 1.99557 1 3.22225M1 3.22225C1 4.45004 3.50133 5.44449 6.58832 5.44449C9.6753 5.44449 12.1766 4.45004 12.1766 3.22225L12.1766 11.8445M1 3.22225V16.5556C1.00112 17.7834 3.50245 18.7779 6.58832 18.7779C8.08487 18.7779 9.43612 18.5412 10.4387 18.1601M1.00112 7.66672C1.00112 8.89451 3.50245 9.88896 6.58944 9.88896C9.67643 9.88896 12.1778 8.89451 12.1778 7.66672M10.5057 13.6946C9.4976 14.0891 8.11505 14.3335 6.58832 14.3335C3.50245 14.3335 1.00112 13.3391 1.00112 12.1113M18.5272 12.4646C20.4909 14.4169 20.4909 17.5836 18.5272 19.5358C16.5635 21.4881 13.3781 21.4881 11.4144 19.5358C9.45066 17.5836 9.45066 14.4169 11.4144 12.4646C13.3781 10.5124 16.5635 10.5124 18.5272 12.4646Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export function DatabaseIcon({ size = 24, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 20.5 22.5"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M19.25 4.25C19.25 5.90685 15.2206 7.25 10.25 7.25C5.27944 7.25 1.25 5.90685 1.25 4.25M19.25 4.25C19.25 2.59315 15.2206 1.25 10.25 1.25C5.27944 1.25 1.25 2.59315 1.25 4.25M19.25 4.25V18.25C19.25 19.91 15.25 21.25 10.25 21.25C5.25 21.25 1.25 19.91 1.25 18.25V4.25M19.25 11.25C19.25 12.91 15.25 14.25 10.25 14.25C5.25 14.25 1.25 12.91 1.25 11.25"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export function HomeIcon({ size = 24, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="m3 10 9-7 9 7" />
      <path d="M5 9v11h14V9" />
      <path d="M9 20v-6h6v6" />
    </svg>
  )
}

export function IntegrationsIcon({ size = 24, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      {/* Two clockwise arcs with arrowheads: data syncing round and round. */}
      <path d="M5 12a7 7 0 0 1 12.73-4.01" />
      <path d="M18.2 4.3v4h-4" />
      <path d="M19 12a7 7 0 0 1-12.73 4.01" />
      <path d="M5.8 19.7v-4h4" />
    </svg>
  )
}

export function AutomationsIcon({ size = 24, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M10 3h6" />
      <path d="M13 3v3" />
      <circle cx="13" cy="14" r="8" />
      <path d="M13 14l4-3.5" />
      <circle cx="13" cy="14" r="1.15" fill="currentColor" stroke="none" />
      <path d="M2.5 9h4.25" />
      <path d="M1.5 13h3.56" />
      <path d="M3.5 17h2.08" />
    </svg>
  )
}

export function UsersIcon({ size = 24, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  )
}

export function ChevronRightIcon({ size = 24, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 8.5 14.5"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M1.25 1.25L7.25 7.25L1.25 13.25"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/** Magnifier with focus-corner brackets — reset zoom to 100%. */
export function ZoomResetIcon({ size = 24, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <circle cx="10.5" cy="10.5" r="8.5" />
      <path d="m16.6 16.6 4.9 4.9" />
      <path d="M7.5 9.5v-2h2" />
      <path d="M11.5 7.5h2v2" />
      <path d="M13.5 11.5v2h-2" />
      <path d="M9.5 13.5h-2v-2" />
    </svg>
  )
}

/** Four arrows converging on a center dot — recenter/fit-view. */
export function RecenterIcon({ size = 24, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M12 2v6M9 5.5 12 8.5l3-3" />
      <path d="M12 22v-6M9 18.5l3-3 3 3" />
      <path d="M2 12h6M5.5 9 8.5 12l-3 3" />
      <path d="M22 12h-6M18.5 9l-3 3 3 3" />
      <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
    </svg>
  )
}

/** Counter-clockwise editor history action. */
export function UndoIcon({ size = 24, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M9 7H4V2" />
      <path d="M4 7a9 9 0 1 1 2.6 12.7" />
    </svg>
  )
}

/** A good answer: the AI Assistant's feedback. */
export function ThumbsUpIcon({ size = 24, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M7 10v12" />
      <path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z" />
    </svg>
  )
}

/** A bad answer: the AI Assistant's feedback. */
export function ThumbsDownIcon({ size = 24, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M17 14V2" />
      <path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 1-3-3.88Z" />
    </svg>
  )
}

/** Clockwise editor history action. */
export function RedoIcon({ size = 24, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M15 7h5V2" />
      <path d="M20 7a9 9 0 1 0-2.6 12.7" />
    </svg>
  )
}

export function ChevronLeftIcon({ size = 24, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 8.5 14.5"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M7.25 1.25L1.25 7.25L7.25 13.25"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export function UserIcon({ size = 24, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  )
}

export function SlidersIcon({ size = 24, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M4 21v-7" />
      <path d="M4 10V3" />
      <path d="M12 21v-9" />
      <path d="M12 8V3" />
      <path d="M20 21v-5" />
      <path d="M20 12V3" />
      <path d="M2 14h4" />
      <path d="M10 8h4" />
      <path d="M18 16h4" />
    </svg>
  )
}

/** Where blanks go in a sort: rows, the last one empty (dashed), and an arrow. */
export function BlanksOrderIcon({ size = 24, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M3 6h10" />
      <path d="M3 12h10" />
      <path d="M3 18h10" strokeDasharray="2 3" />
      <path d="M19 4v16" />
      <path d="m16 17 3 3 3-3" />
    </svg>
  )
}

export function SignOutIcon({ size = 24, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="M16 17l5-5-5-5" />
      <path d="M21 12H9" />
    </svg>
  )
}

export function KeyIcon({ size = 24, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <circle cx="7.5" cy="14.5" r="5.5" />
      <path d="M12 10l9-9" />
      <path d="M16 5l3 3" />
      <path d="M18 3l3 3" />
    </svg>
  )
}

export function LockIcon({ size = 24, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  )
}

/** The light theme. */
export function SunIcon({ size = 24, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </svg>
  )
}

/** The dark theme. */
export function MoonIcon({ size = 24, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
    </svg>
  )
}

/** The computer's own setting, such as the System theme. */
export function MonitorIcon({ size = 24, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <path d="M8 21h8M12 17v4" />
    </svg>
  )
}

/** Sorted by name: an arrow down beside A over Z. */
export function SortAlphabeticalIcon({ size = 24, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M3 16l4 4 4-4M7 20V4" />
      <path d="M14.5 10V6.5a2.5 2.5 0 0 1 5 0V10M14.5 8h5" />
      <path d="M14.5 14h5l-5 6h5" />
    </svg>
  )
}

/** A calendar, such as for sorting by when something was last opened. */
export function CalendarIcon({ size = 24, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M8 3v4M16 3v4M3 10h18" />
    </svg>
  )
}

/** The sidebar always collapsed: a rail with a chevron pointing into it. */
export function SidebarCollapsedIcon({ size = 24, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M8 4v16M16 9l-3 3 3 3" />
    </svg>
  )
}

/** The sidebar always expanded: a rail with a chevron pointing out of it. */
export function SidebarExpandedIcon({ size = 24, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M8 4v16M13 9l3 3-3 3" />
    </svg>
  )
}

/**
 * The sidebar dynamic: a rail that widens while the pointer is on it. The
 * cursor is filled and breaks out of the window's corner, so it still reads
 * as a cursor at 12px.
 */
export function SidebarDynamicIcon({ size = 24, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M21 12V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h6M8 4v16" />
      <path
        fill="currentColor"
        d="M13.03 13.68a.5.5 0 0 1 .65-.65l8 3.1a.5.5 0 0 1-.03.94l-3.06.95a1 1 0 0 0-.66.66l-.95 3.06a.5.5 0 0 1-.94.03z"
      />
    </svg>
  )
}

export function UnlockIcon({ size = 24, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 7.9-1" />
    </svg>
  )
}

export function BuildingIcon({ size = 24, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M3 21h18" />
      <path d="M5 21V9h4v12" />
      <path d="M9 21V3h6v18" />
      <path d="M15 21V9h4v12" />
      <path d="M11 7h2" />
      <path d="M11 11h2" />
      <path d="M11 15h2" />
      <path d="M6.5 13h1" />
      <path d="M6.5 17h1" />
      <path d="M16.5 13h1" />
      <path d="M16.5 17h1" />
    </svg>
  )
}

export function SendIcon({ size = 24, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 22 22"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M21 1.00003L10 12M21 1.00003L14 21L10 12M21 1.00003L1.00001 8.00003L10 12"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export function PrinterIcon({ size = 24, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 22 22"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M5 8V1H17V8M5 17H3C2.46957 17 1.96086 16.7893 1.58579 16.4142C1.21071 16.0391 1 15.5304 1 15V10C1 9.46957 1.21071 8.96086 1.58579 8.58579C1.96086 8.21071 2.46957 8 3 8H19C19.5304 8 20.0391 8.21071 20.4142 8.58579C20.7893 8.96086 21 9.46957 21 10V15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H17M5 13H17V21H5V13Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export function GearIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06A2 2 0 1 1 7.04 4.37l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9c0 .66.39 1.26 1 1.51H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51.49Z" />
    </svg>
  )
}

export function KebabIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor" {...props}>
      <circle cx="8" cy="3" r="1.5" />
      <circle cx="8" cy="8" r="1.5" />
      <circle cx="8" cy="13" r="1.5" />
    </svg>
  )
}

export function CloseIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M12 4L4 12M4 4l8 8" />
    </svg>
  )
}

/** A link: two chain links (the Export menu's Link row). */
export function LinkIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M6.6 9.4a2.6 2.6 0 0 0 3.7 0l2.6-2.6a2.6 2.6 0 0 0-3.7-3.7l-.7.7" />
      <path d="M9.4 6.6a2.6 2.6 0 0 0-3.7 0L3.1 9.2a2.6 2.6 0 0 0 3.7 3.7l.7-.7" />
    </svg>
  )
}

export function PaperclipIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M13.2 7.6 8.1 12.7a3.3 3.3 0 0 1-4.7-4.7l5.4-5.4a2.2 2.2 0 0 1 3.1 3.1L6.5 11.1a1.1 1.1 0 0 1-1.6-1.6l4.8-4.8" />
    </svg>
  )
}

export function FileIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M3 1.8h6.2L13 5.6v8.6H3V1.8Z" />
      <path d="M9.2 1.8v3.8H13" />
    </svg>
  )
}

export function ImageIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <rect x="2" y="2.5" width="12" height="11" rx="1.5" />
      <circle cx="5.8" cy="6.2" r="1.2" />
      <path d="m2.5 12 3.6-3.6 2.6 2.6 1.8-1.8 3 3" />
    </svg>
  )
}

export function CopyIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <rect x="5.5" y="5.5" width="9" height="9" rx="1.5" />
      <path d="M3 10.5H2.5A1.5 1.5 0 0 1 1 9V2.5A1.5 1.5 0 0 1 2.5 1H9a1.5 1.5 0 0 1 1.5 1.5V3" />
    </svg>
  )
}

export function DuplicateIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <rect x="5.5" y="5.5" width="9" height="9" rx="1.5" />
      <path d="M3 10.5H2.5A1.5 1.5 0 0 1 1 9V2.5A1.5 1.5 0 0 1 2.5 1H9a1.5 1.5 0 0 1 1.5 1.5V3" />
      <path d="M10 8.4v3.2" />
      <path d="M8.4 10h3.2" />
    </svg>
  )
}

/** Copying in from elsewhere: the copy icon with an arrow down into its front square. */
export function CopyInIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <rect x="5.5" y="5.5" width="9" height="9" rx="1.5" />
      <path d="M3 10.5H2.5A1.5 1.5 0 0 1 1 9V2.5A1.5 1.5 0 0 1 2.5 1H9a1.5 1.5 0 0 1 1.5 1.5V3" />
      <path d="M10 7.6v4.2" />
      <path d="M8.3 10.2 10 11.9l1.7-1.7" />
    </svg>
  )
}

/** Open as a side panel: a window with a panel along its right edge. */
export function SidePanelIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M15 4v16" />
    </svg>
  )
}

/** Minimize: a short bar along the bottom, as a window's minimize button. */
export function MinimizeIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      {...props}
    >
      <path d="M3.5 12.5h9" />
    </svg>
  )
}

/** Open as a pop-up: a window with a smaller window floating inside it. */
export function PopUpIcon({ size = 16, ...props }: IconProps) {
  // A frame open at its top-right, where a small window sits, and at its
  // bottom-left, where an arrow comes in toward the middle; drawn like Expand.
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M7 2.75H4.25a1.5 1.5 0 0 0-1.5 1.5V7.5" />
      <path d="M8.5 13.25h2.75a1.5 1.5 0 0 0 1.5-1.5V9" />
      <rect x="9.5" y="1.75" width="4.75" height="4.75" rx="1" />
      <path d="M1.75 14.25 7 9" />
      <path d="M4 9h3v3" />
    </svg>
  )
}

/** Expand everything: chevrons pointing apart. */
export function UnfoldIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="m7 15 5 5 5-5" />
      <path d="m7 9 5-5 5 5" />
    </svg>
  )
}

/** Collapse everything: chevrons pointing together. */
export function FoldIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="m7 20 5-5 5 5" />
      <path d="m7 4 5 5 5-5" />
    </svg>
  )
}

export function MailIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <rect x="2" y="3.5" width="12" height="9" rx="1.5" />
      <path d="m3 5 5 3.8L13 5" />
    </svg>
  )
}

export function CsvFileIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M3 1.8h6.2L13 5.6v8.6H3V1.8Z" />
      <path d="M9.2 1.8v3.8H13" />
      <path d="M5.2 8.2h5.6" />
      <path d="M5.2 10.5h5.6" />
      <path d="M6.8 8.2v4" />
      <path d="M9.4 8.2v4" />
    </svg>
  )
}

export function ExcelFileIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M5.5 2h7v12h-7" />
      <path d="M5.5 4.4h7" />
      <path d="M5.5 7h7" />
      <path d="M5.5 9.6h7" />
      <path d="M9 4.4V14" />
      <path d="M1.8 4.5h4.8v7H1.8v-7Z" />
      <path d="m3.2 6.4 2 3.2" />
      <path d="m5.2 6.4-2 3.2" />
    </svg>
  )
}

export function PdfFileIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M3 1.8h6.2L13 5.6v8.6H3V1.8Z" />
      <path d="M9.2 1.8v3.8H13" />
      <path d="M5.2 8.2h5.6" />
      <path d="M5.2 10.5h5.6" />
      <path d="M5.2 12.4h3.2" />
    </svg>
  )
}

export function TeamsIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M5.3 5.5H1.8v5.8c0 1.1.9 2 2 2h2.4c1.1 0 2-.9 2-2V9.7" />
      <path d="M3.1 7.4H6" />
      <path d="M4.6 7.4v3.8" />
      <circle cx="6.8" cy="2.7" r="1.6" />
      <circle cx="11.5" cy="3.4" r="1.3" />
      <path d="M9.3 6.4h3.8c.7 0 1.2.5 1.2 1.2v2c0 1.5-1.2 2.7-2.7 2.7h-.4" />
    </svg>
  )
}

export function ConnectedShareIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M5.1 8 10.9 4" />
      <path d="M5.1 8 10.9 12" />
      <circle cx="3.3" cy="8" r="2" />
      <circle cx="12.7" cy="3" r="2" />
      <circle cx="12.7" cy="13" r="2" />
    </svg>
  )
}

export function CheckIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M3.5 8.5L6.5 11.5L12.5 4.5" />
    </svg>
  )
}

export function GripIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor" {...props}>
      <circle cx="5.5" cy="3" r="1.5" />
      <circle cx="10.5" cy="3" r="1.5" />
      <circle cx="5.5" cy="8" r="1.5" />
      <circle cx="10.5" cy="8" r="1.5" />
      <circle cx="5.5" cy="13" r="1.5" />
      <circle cx="10.5" cy="13" r="1.5" />
    </svg>
  )
}

export function PlusIcon({ size = 24, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M8 1V15M1 8H15"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/** A row with an up arrow and a plus badge: insert a row above this one. */
export function PlayIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M5.2 3.35Q4.35 2.88 4.35 3.85V12.15Q4.35 13.12 5.2 12.65L12.35 8.68Q13.58 8 12.35 7.32L5.2 3.35Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export function FlaskIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M5.5 1.75h5" />
      <path d="M6.5 1.75v4.1l-3.7 6.1a1.5 1.5 0 0 0 1.28 2.3h7.84a1.5 1.5 0 0 0 1.28-2.3l-3.7-6.1v-4.1" />
      <path d="M4.5 9.15h7" />
    </svg>
  )
}

export function PowerIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M8 1.5v6" />
      <path d="M4.35 3.75a5.25 5.25 0 1 0 7.3 0" />
    </svg>
  )
}

export function CalendarClockIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M5 3v3M15 3v3M4 8h13M4 5h13a2 2 0 0 1 2 2v5" />
      <path d="M17 13.5a4.5 4.5 0 1 0 0 9 4.5 4.5 0 0 0 0-9Z" />
      <path d="M17 15.5v2.5l1.7 1" />
      <path d="M13 21H6a2 2 0 0 1-2-2V5" />
    </svg>
  )
}

// ───────────────────────────────────────────
// Toolbar icons — for badge buttons in the view row
// ───────────────────────────────────────────

export function RecordFolderIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M5.2 7.2V4.6l2.4-2.4h4.2a.9.9 0 0 1 .9.9v5.1" />
      <path d="M7.6 2.2v2.4H5.2" />
      <path d="M9.2 5.8h2.1" />
      <path d="M8.1 7.4h3.2" />
      <path d="M1.8 13.9v-7c0-.5.4-.9.9-.9h.8" />
      <path d="M1.8 13.9 4.2 8.6h3.6l1.1-1.3h4.6c.6 0 1 .6.8 1.2L13 13.2a1 1 0 0 1-1 .7H1.8Z" />
    </svg>
  )
}

export function SaveIcon({ size = 24, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M4 3H16L21 8V21H4V3Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8 3V10H16V3"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8 21V14H17V21"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/** Grid-like icon for Columns badge button */
export function ColumnsIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <rect x="1" y="1" width="14" height="14" rx="2" />
      <line x1="5.5" y1="1" x2="5.5" y2="15" />
      <line x1="10.5" y1="1" x2="10.5" y2="15" />
    </svg>
  )
}

/** Grouped rows icon */
export function GroupsIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M2 2.5h12" />
      <path d="M9 6.5h5" />
      <path d="M9 9.5h5" />
      <path d="M2 13.5h12" />
      <path d="M2 8h4.25" />
      <path d="M4.35 5.75 6.6 8l-2.25 2.25" />
    </svg>
  )
}

/** Up/down arrows icon for Sorts badge button */
export function SortsIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M5 11V3M5 3L2.5 5.5M5 3L7.5 5.5" />
      <path d="M11 5v8m0 0l2.5-2.5M11 13l-2.5-2.5" />
    </svg>
  )
}

export function SortAscendingIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M5 13V3" />
      <path d="M2.5 5.5 5 3l2.5 2.5" />
      <path d="M9 4h4" />
      <path d="M9 8h3" />
      <path d="M9 12h2" />
    </svg>
  )
}

export function SortDescendingIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M5 3v10" />
      <path d="M2.5 10.5 5 13l2.5-2.5" />
      <path d="M9 4h2" />
      <path d="M9 8h3" />
      <path d="M9 12h4" />
    </svg>
  )
}

/** Trash can icon for delete actions in modals */
export function TrashIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M2 4h12M5.33 4V2.67a1.33 1.33 0 0 1 1.34-1.34h2.66a1.33 1.33 0 0 1 1.34 1.34V4m2 0v9.33a1.33 1.33 0 0 1-1.34 1.34H4.67a1.33 1.33 0 0 1-1.34-1.34V4" />
    </svg>
  )
}

/** Two vertical bars: pause. */
export function PauseIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M5.5 3.5v9M10.5 3.5v9" />
    </svg>
  )
}

/** A rounded square: stopping something that's running. */
export function StopIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <rect x="3.75" y="3.75" width="8.5" height="8.5" rx="1.5" />
    </svg>
  )
}

/** A chain link broken in two: detaching something from what syncs it. */
export function UnlinkIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M6.5 4.5 7.6 3.4a2.5 2.5 0 0 1 3.5 3.5L10 8" />
      <path d="M9.5 11.5 8.4 12.6a2.5 2.5 0 0 1-3.5-3.5L6 8" />
      <path d="M2.5 2.5l1.5 1.5M13.5 13.5 12 12" />
    </svg>
  )
}

/** Rounded record panel with an inset right-side divider. */
export function RecordPreviewIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <rect x="1.5" y="2" width="13" height="12" rx="3" />
      <path d="M10.5 4.5v7" />
    </svg>
  )
}

/** Expand icon (diagonal arrows outward) for group expand */
export function ExpandIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M10 2h4v4M6 14H2v-4M14 2L9.5 6.5M2 14l4.5-4.5" />
    </svg>
  )
}

/**
 * Collapse icon (diagonal arrows inward) for group collapse. Shares the
 * bottom-left/top-right diagonal with ExpandIcon so toggling only flips the
 * arrowheads, never the line positions.
 */
export function CollapseIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M14 2L9.5 6.5M14 6.5H9.5V2M2 14l4.5-4.5M2 9.5h4.5V14" />
    </svg>
  )
}

interface FolderIconProps extends IconProps {
  filled?: boolean
}

/** Folder icon for view folders */
export function FolderIcon({ size = 16, filled = false, ...props }: FolderIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M14.5 13.5h-13A.5.5 0 0 1 1 13V3a.5.5 0 0 1 .5-.5h4l2 2h7a.5.5 0 0 1 .5.5v8a.5.5 0 0 1-.5.5Z" />
    </svg>
  )
}

/**
 * Open in a new window or app: a box with an arrow leaving its top-right
 * corner, as Chrome's downloads draw Open. Drawn on DownloadIcon's grid
 * (20 units, a 2-unit line), its box's bottom the same as Download's tray,
 * so the two read as one size side by side.
 */
export function OpenInNewIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M9 1H3a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-6" />
      <path d="M13 1h6v6" />
      <path d="M19 1 9 11" />
    </svg>
  )
}

/** Open folder icon for expanded view folders */
export function FolderOpenIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M1.5 12.4V3.5a.5.5 0 0 1 .5-.5h3.6l1.8 2h6.1a.5.5 0 0 1 .5.5v2.4" />
      <path d="M1.5 12.4 4.85 8a1 1 0 0 1 .8-.4h8.85a.5.5 0 0 1 .4.8l-3.2 4.25a1 1 0 0 1-.8.4H1.9a.5.5 0 0 1-.4-.8Z" />
    </svg>
  )
}

/** Star icon for view favorites */
export function StarIcon({
  size = 16,
  filled = false,
  ...props
}: IconProps & { filled?: boolean }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M8 1.5l1.85 3.75L14 5.92l-2.75 2.68.65 3.78L8 10.42l-3.9 1.96.65-3.78L2 5.92l4.15-.67L8 1.5Z" />
    </svg>
  )
}

/** Heart for favorites: filled when the page is a favorite, outlined when it isn't. */
export function HeartIcon({
  size = 16,
  filled = false,
  ...props
}: IconProps & { filled?: boolean }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M8 13.5S1.75 9.85 1.75 5.7C1.75 3.9 3.2 2.5 4.95 2.5c1.25 0 2.4.7 3.05 1.75.65-1.05 1.8-1.75 3.05-1.75 1.75 0 3.2 1.4 3.2 3.2 0 4.15-6.25 7.8-6.25 7.8Z" />
    </svg>
  )
}

/**
 * An eye with a slash through it, for pages hidden from the sidebar's menus:
 * filled when the page is hidden (or a menu shows its hidden rows), outlined
 * as the action to hide one. The slash is cut clear of the eye.
 */
export function EyeOffIcon({
  size = 16,
  filled = false,
  ...props
}: IconProps & { filled?: boolean }) {
  const maskId = useId()
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <defs>
        <mask id={maskId}>
          <rect width="16" height="16" fill="white" stroke="none" />
          <line x1="2.5" y1="2.5" x2="13.5" y2="13.5" stroke="black" strokeWidth="3.5" />
          {filled && <circle cx="8" cy="8" r="1.75" fill="black" stroke="none" />}
        </mask>
      </defs>
      <g mask={`url(#${maskId})`}>
        <path
          d="M1.75 8c1.5-2.9 3.6-4.25 6.25-4.25S12.75 5.1 14.25 8c-1.5 2.9-3.6 4.25-6.25 4.25S3.25 10.9 1.75 8Z"
          fill={filled ? 'currentColor' : 'none'}
        />
        {!filled && <circle cx="8" cy="8" r="2" />}
      </g>
      <line x1="2.5" y1="2.5" x2="13.5" y2="13.5" />
    </svg>
  )
}

/** Search/magnifier icon */
export function SearchIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      {...props}
    >
      <circle cx="7" cy="7" r="5" />
      <line x1="11" y1="11" x2="14.5" y2="14.5" />
    </svg>
  )
}

/** Info icon (circle with i) */
export function InfoIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <circle cx="8" cy="8" r="7" />
      <line x1="8" y1="7" x2="8" y2="11" />
      <circle cx="8" cy="5" r="0.5" fill="currentColor" stroke="none" />
    </svg>
  )
}

/** Code brackets icon */
export function CodeIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M5.5 4.5 2 8l3.5 3.5" />
      <path d="M10.5 4.5 14 8l-3.5 3.5" />
      <path d="M9 3 7 13" />
    </svg>
  )
}

interface PinIconProps extends IconProps {
  filled?: boolean
}

/** Pin icon for column pinning */
export function PinIcon({ size = 16, filled = false, ...props }: PinIconProps) {
  const pinPath =
    'M1.5 22.5L7.4 14.4L3.4 10.4C3 10 3 9.3 3.5 9C5.4 7.8 8.2 7.5 10.3 7.9L15.1 3.1L15.3 1.5C15.4 0.9 16.2 0.6 16.7 1.1L22.9 7.3C23.4 7.8 23.1 8.6 22.5 8.7L20.9 8.9L16.1 13.7C16.5 15.8 16.2 18.6 15 20.5C14.7 21 14 21 13.6 20.6L9.6 16.6L1.5 22.5Z'

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="miter"
      {...props}
    >
      <path d={pinPath} />
    </svg>
  )
}

/** Chat bubble with a sparkle accent — the icon for every AI Assistant surface. */
export function SparkleChatIcon({ size = 24, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M21 11.5a8.38 8.38 0 0 1-8.5 8.5H4l2.4-3.6A8.38 8.38 0 1 1 21 11.5Z" />
      <path d="M12.5 8l.6 1.5 1.5.6-1.5.6-.6 1.5-.6-1.5-1.5-.6 1.5-.6.6-1.5Z" />
    </svg>
  )
}

export function SpinnerIcon({ size = 16, className, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className={`animate-spin ${className ?? ''}`}
      {...props}
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" opacity={0.25} />
      <path fill="currentColor" opacity={0.75} d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  )
}

/** Counter-clockwise restore arrow with clock hands, for version history. */
export function HistoryIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M3 12a9 9 0 1 0 2.6-6.4" />
      <path d="M3 4v5h5" />
      <path d="M12 8v4l3 2" />
    </svg>
  )
}

/** Circular arrow for refreshing a view in place. */
export function RefreshIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M20 12a8 8 0 1 1-2.34-5.66" />
      <path d="M20 4v5h-5" />
    </svg>
  )
}

/** An update: a large four-point sparkle with two small ones (the update notices). */
export function UpdateSparklesIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M11 4.5c.5 3.6 1.9 5 5.5 5.5-3.6.5-5 1.9-5.5 5.5-.5-3.6-1.9-5-5.5-5.5 3.6-.5 5-1.9 5.5-5.5Z" />
      <path d="M18.5 2c.25 1.6.9 2.25 2.5 2.5-1.6.25-2.25.9-2.5 2.5-.25-1.6-.9-2.25-2.5-2.5 1.6-.25 2.25-.9 2.5-2.5Z" />
      <path d="M5.5 15.5c.25 1.6.9 2.25 2.5 2.5-1.6.25-2.25.9-2.5 2.5-.25-1.6-.9-2.25-2.5-2.5 1.6-.25 2.25-.9 2.5-2.5Z" />
      <circle cx="19.5" cy="15" r="1" />
      <circle cx="3.5" cy="6.5" r="0.8" />
    </svg>
  )
}
